import React from 'react';
import PropTypes from 'prop-types';
/* helpers */
import { getClassName, isUserAgentMobile } from '../../utils/info-helper';
/* styles */
import './input.scss';
import { KeyboardContext } from '../../context/keyboard-context';

export class Input extends React.PureComponent {
    static contextType = KeyboardContext;

    state = {
        value: this.props.value,
        prevValue: this.props.value,
        isFocused: false
    };

    static getDerivedStateFromProps(props, state) {
        if (props.value !== state.prevValue) {
            return {
                prevValue: props.value,
                value: props.value
            };
        }
        return null;
    }

    componentDidMount() {
        if (this.props.autoFocus) {
            this.autoFocusTimeout = setTimeout(() => {
                this.input.focus();
            }, this.props.autoFocusDelay);
        }
        if (this.input && this.input.value && this.input.value !== this.state.value) {
            this.onChange(null, this.input.value);
        }
    }

    componentWillUnmount() {
        if (this.context && this.context.activeInput === this.input) {
            this.context.setActiveInput(null);
        }
        if (this.keyboardTimeout) {
            clearTimeout(this.keyboardTimeout);
        }
        if (this.autoFocusTimeout) {
            clearTimeout(this.autoFocusTimeout);
        }
        if (this.changeDebounce) {
            clearTimeout(this.changeDebounce);
        }
    }

    onOuterDivBlur = () => {
        this.setState({ isFocused: false });
    };

    onInput = event => {
        if (this.inputTimeout) {
            clearTimeout(this.inputTimeout);
        }
        const value = event.target.value;
        this.inputTimeout = setTimeout(() => this.onChange(event, value), 0);
    };

    onChange = (event, value) => {
        if (this.inputTimeout) {
            clearTimeout(this.inputTimeout);
        }
        this.setState({ value: value !== undefined ? value : event.target.value }, () => {
            if (this.changeDebounce) {
                clearTimeout(this.changeDebounce);
            }
            this.changeDebounce = setTimeout(() => {
                this.props.onChange(this.state.value);
            }, this.props.changeDebounce);
        });
    };

    onFocusChange = isFocused => {
        if (this.props.onFocusChange) {
            this.props.onFocusChange(isFocused);
        }
        if (isFocused !== this.state.isFocused) {
            this.setState({ isFocused });
        }
        if ((this.props.onScreenKeyboardEnabled || this.props.showKeyboard)
            && !this.props.disabled && !this.props.readOnly) {
            if (isFocused && this.props.type !== 'button') {
                if (!this.props.showKeyboard && this.context && this.context.activeInput !== this.input) {
                    this.context.setActiveInput(this.input);
                }
                if (this.keyboardTimeout) {
                    clearTimeout(this.keyboardTimeout);
                }
            } else {
                this.keyboardTimeout = setTimeout(() => {
                    if (this.context && this.context.activeInput === this.input) {
                        this.context.setActiveInput(null);
                    }
                }, 300);
            }
        }
    };

    setFocus = () => {
        this.input.focus();
    };

    showKeyboard = () => {
        if (this.context && !isUserAgentMobile()) {
            if (this.context.activeInput !== this.input) {
                this.context.setActiveInput(this.input);
            } else {
                this.context.setActiveInput(null);
            }
        }
        this.input.focus();
    };

    getIconElement = (iconClass) => (<span className={`icon sten-input__icon ${iconClass}`} />);

    render() {
        const className = getClassName('sten-input', this.props.className, {
            'sten-input--disabled': this.props.disabled,
            'sten-input--focused': !this.props.disabled && this.state.isFocused,
            'sten-input--warning': this.props.invalid && this.props.warning,
            'sten-input--invalid': this.props.invalid && !this.props.warning
        });


        const keyboardIconClass = getClassName('icon sten-input__icon icon-keyboard sten-input__keyboard', {
            'sten-input__keyboard--active': this.context && this.context.activeInput === this.input
        });

        let inputValue = typeof this.state.value === 'number' || typeof this.state.value === 'string'
            ? this.state.value
            : '';

        if (this.props.readOnly && this.props.displayValue) {
            inputValue = this.props.displayValue;
        }

        return (
            <div
                className={className}
                title={this.props.disabled && this.props.disabledMessage ? this.props.disabledMessage : ''}
                onClick={!this.props.disabled ? this.props.onClick : undefined}
                onBlur={this.onOuterDivBlur}
            >
                <input
                    key="input"
                    onInput={this.onInput}
                    autoComplete={this.props.autoComplete}
                    // autoFocus={this.props.autoFocus}
                    readOnly={this.props.readOnly}
                    ref={(input) => { this.input = input; }}
                    type={this.props.type}
                    className="sten-input__input"
                    name={this.props.name || this.props.id}
                    id={this.props.id}
                    value={inputValue}
                    placeholder={this.props.placeholder}
                    onChange={this.onChange}
                    onFocus={this.onFocusChange.bind(this, true)}
                    onBlur={this.onFocusChange.bind(this, false)}
                    disabled={this.props.disabled}
                    {...this.props.inputFieldProps}
                />
                {!this.props.disabled && this.props.showKeyboard && (
                    <div className="sten-input__item sten-input__item--clickable" onClick={this.showKeyboard}>
                        <span key="showkeyboard" className={keyboardIconClass} />
                    </div>
                )}
            </div>
        );
    }
}

Input.propTypes = {
    activeInput: PropTypes.objectOf(PropTypes.any),
    autoComplete: PropTypes.string,
    autoFocus: PropTypes.bool,
    autoFocusDelay: PropTypes.number,
    changeDebounce: PropTypes.number,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    disabledMessage: PropTypes.string,
    displayValue: PropTypes.string,
    id: PropTypes.string,
    inputFieldProps: PropTypes.objectOf(PropTypes.any),
    invalid: PropTypes.bool,
    name: PropTypes.string,
    onChange: PropTypes.func,
    onClick: PropTypes.func,
    onFocusChange: PropTypes.func,
    onScreenKeyboardEnabled: PropTypes.bool,
    placeholder: PropTypes.string,
    readOnly: PropTypes.bool,
    showKeyboard: PropTypes.bool,
    type: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    warning: PropTypes.bool
};

Input.defaultProps = {
    activeInput: null,
    autoComplete: undefined,
    autoFocus: false,
    autoFocusDelay: 500,
    changeDebounce: 50,
    className: '',
    disabled: false,
    disabledMessage: '',
    displayValue: undefined,
    id: undefined,
    inputFieldProps: null,
    invalid: false,
    name: '',
    onChange: undefined,
    onClick: undefined,
    onFocusChange: undefined,
    onScreenKeyboardEnabled: false,
    placeholder: '',
    readOnly: false,
    showKeyboard: false,
    type: 'text',
    value: '',
    warning: false
};

export default Input;
